import {COLORS} from "./configs/config";
import {Box, CircularProgress, Grid, Skeleton, Typography} from "@mui/material";
import {useAppContext} from "../../../../contexts/AppContext";
import {useCommonItems} from "../../../../contexts/CommonItensProvider";
import {hasPermission} from "../../../../configs/functions";
import React, {useCallback, useEffect, useState} from "react";
import {getHomeData} from "./configs/functions";
import {Helmet} from "react-helmet-async";
import PageTitle from "../../../common/PageTitle";
import {HomePageIcon} from "../../../common/Icons";
import {AlertRow, BannerMetrics, DashCard, InfoRow, MODULE_LIST} from "./components/HomeCards";
import {CategoryOutlined, DevicesOutlined, PeopleOutlined} from "@mui/icons-material";
import CardOption from "../../../common/CardOption";
import {verifyUserAbilities} from "../../../config/functions";

export default function Home() {
    const { dadosUsuario } = useAppContext();
    const { exibirAlerta } = useCommonItems();

    const isAdmin = hasPermission(['admin', 'dashboard']);

    const [loading, setLoading] = useState(false);

    const [data, setData] = useState(null);
    const [empresas] = useState([]);

    const [abilities, setAbilities] = useState({});
    const [loadingAbilities, setLoadingAbilities] = useState(true);

    useEffect(() => {
        const carregarPermissoes = async () => {
            const resultados = {};

            for (const item of MODULE_LIST) {
                resultados[item.rota] = await verifyUserAbilities({ rota: item.rota });
            }

            setAbilities(resultados);
            setLoadingAbilities(false);
        };

        carregarPermissoes();
    }, []);

    const load = useCallback(() => {
        setLoading(true);

        getHomeData({ companies: dadosUsuario?.pessoa?.empresa?.id, isFilter: true })
            .then((res) => setData(res.data))
            .catch(() => exibirAlerta('Ops!', 'Erro ao carregar informações', 'error'))
            .finally(() => setLoading(false));
    }, [empresas]);

    useEffect(() => { load(); }, [load]);

    const alertas = data?.alertas?.items ?? [];
    const totalAlertas = data?.alertas?.total ?? 0;
    const empresa = data?.dadosEmpresa;

    return (
        <>
            <Helmet title="Página Inicial" defer={false} />

            <Box sx={{ p: { xs: 2, md: 3 }, minHeight: '100vh' }}>
                <Box mb={3}>
                    <PageTitle icon={<HomePageIcon fontSize="large" />} title="Página Inicial" />
                </Box>

                <Box mb={2}>
                    <BannerMetrics data={data} loading={loading} isAdmin={isAdmin} />
                </Box>

                <Grid container spacing={1.5} mb={1.5}>
                    {isAdmin && (
                        <Grid item xs={12} md={6}>
                            <DashCard title="Alertas operacionais" data={totalAlertas}>
                                {loading
                                    ? [1,2,3].map((i) => <Skeleton key={i} variant="text" sx={{ bgcolor: COLORS.kpiBg, mb: 0.5 }} />)
                                    : alertas.length > 0
                                        ? alertas.map((a, i) => (
                                            <AlertRow
                                                key={i}
                                                label={a.nome}
                                                valor={a.total}
                                                tipo={a.total > 0 ? 'warn' : 'ok'}
                                            />
                                        ))
                                        : <Typography variant="caption" sx={{ color: COLORS.tDim }}>Nenhum alerta</Typography>
                                }
                            </DashCard>
                        </Grid>
                    )}

                    {isAdmin && (
                        <Grid item xs={12} md={6}>
                            <DashCard title="Empresa">
                                {loading
                                    ? [1,2,3].map((i) => <Skeleton key={i} variant="text" sx={{ bgcolor: COLORS.kpiBg, mb: 0.5 }} />)
                                    : <>
                                        <InfoRow icon={<PeopleOutlined fontSize="inherit" />} label={`${(empresa?.pessoas ?? 0).toLocaleString('pt-BR')} funcionários`} />
                                        <InfoRow icon={<DevicesOutlined fontSize="inherit" />} label={`${(empresa?.equipamentos ?? 0).toLocaleString('pt-BR')} equipamentos ativos`} />
                                        <InfoRow icon={<CategoryOutlined fontSize="inherit" />} label={`${(empresa?.produtos ?? 0).toLocaleString('pt-BR')} produtos cadastrados`} />
                                    </>
                                }
                            </DashCard>
                        </Grid>
                    )}
                </Grid>

                <Typography variant="caption" sx={{
                    color: COLORS.tDim, fontWeight: 600, display: 'block',
                    mb: 1, textTransform: 'uppercase', letterSpacing: '.04em', fontSize: 10,
                }}>
                    Módulos
                </Typography>

                <Grid container spacing={1.5}>
                    {loadingAbilities
                        ? (
                            <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress size={30} color='primary' />
                            </Grid>
                        )
                        : MODULE_LIST.map((m, i) => {
                            const temPermissao = abilities[m.rota]?.data?.success;

                            return (
                                <Grid
                                    key={i}
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                    sx={{
                                        opacity: temPermissao ? 1 : 0.5,
                                        pointerEvents: temPermissao ? 'auto' : 'none',
                                    }}
                                >
                                    <CardOption
                                        title={m.title}
                                        description={m.desc}
                                        url={m.url}
                                        hasPermisson={temPermissao}
                                        sx={{
                                            backgroundColor: temPermissao ? m.color : 'gray',
                                            color: temPermissao ? 'black' : 'gray',
                                        }}
                                    />
                                </Grid>
                            );
                        })
                    }
                </Grid>
            </Box>
        </>
    );
}